import React, { useCallback, useRef } from "react";

/** Unlock a readonly input placed to block Chrome autofill until interaction. */
export const unlockBrowserAutofillInput = (el) => {
  if (el && el.readOnly) {
    el.readOnly = false;
    el.removeAttribute("readonly");
  }
};

/** Props that discourage Chrome / password-manager autofill on order forms. */
export const browserAutofillGuardProps = ({
  disabled = false,
  type,
  inputMode,
  onMouseDown,
  onFocus,
  unlockRef,
} = {}) => {
  const unlock = () => unlockBrowserAutofillInput(unlockRef?.current);
  return {
    readOnly: disabled ? undefined : true,
    autoComplete: "one-time-code",
    spellCheck: false,
    "data-form-type": "other",
    "data-lpignore": "true",
    "data-1p-ignore": true,
    type: type === "email" ? "text" : type,
    inputMode: type === "email" ? "email" : inputMode,
    onMouseDown: (e) => {
      unlock();
      onMouseDown?.(e);
    },
    onFocus: (e) => {
      unlock();
      onFocus?.(e);
    },
  };
};

/** Text input. Optional `prefix` / `suffix` adornments (e.g. "+1", "min"). */
const LpInput = React.forwardRef(function LpInput(
  {
    prefix,
    suffix,
    error = false,
    className = "",
    disableBrowserAutofill = false,
    type,
    inputMode,
    onMouseDown,
    onFocus,
    disabled,
    ...rest
  },
  ref
) {
  const inputRef = useRef(null);
  const setRef = useCallback(
    (node) => {
      inputRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    },
    [ref]
  );

  const inputCls = ["lp-input", error ? "lp-input--error" : "", className]
    .filter(Boolean)
    .join(" ");

  const autofillProps = disableBrowserAutofill
    ? browserAutofillGuardProps({
        disabled,
        type,
        inputMode,
        onMouseDown,
        onFocus,
        unlockRef: inputRef,
      })
    : { type, inputMode, onMouseDown, onFocus };

  const inputEl = (
    <input
      ref={setRef}
      className={inputCls}
      disabled={disabled}
      {...rest}
      {...autofillProps}
    />
  );

  if (prefix || suffix) {
    const wrapCls = [
      "lp-input-wrap",
      prefix ? "lp-input-wrap--with-prefix" : "",
      suffix ? "lp-input-wrap--with-suffix" : "",
    ]
      .filter(Boolean)
      .join(" ");
    return (
      <span className={wrapCls}>
        {prefix ? <span className="lp-input-prefix">{prefix}</span> : null}
        {inputEl}
        {suffix ? <span className="lp-input-suffix">{suffix}</span> : null}
      </span>
    );
  }
  return inputEl;
});

export default React.memo(LpInput);
